import { collection, doc, getDocs, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import React, { useState, useEffect, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { HiCheck, HiXMark, HiPencilSquare, HiArrowPath, HiArrowUpTray, HiDocumentText } from 'react-icons/hi2';

interface SourceMaterial {
  id: string;
  sourceDocument: string;
  content: string;
  topicTags: string[];
  dataTypeTags: string[];
  version: number;
  status: string;
  embeddingVersion: number;
  embeddingGeneratedAt: any;
  vectorIndexed: boolean;
  created: any;
  lastUpdated: any;
}

interface chunkFormData {
  id: string;
  content: string;
  sourceDocument: string;
  topicTags: string[];
  dataTypeTags: string[];
  topicTagsInput: string;
  dataTypeTagsInput: string;
}

const SourceMaterialManagement: React.FC = () => {
  const [sourceMaterial, setMaterial] = useState<SourceMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingChunk, setEditingChunk] = useState<SourceMaterial | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [updatingChunks, setUpdatingChunks] = useState<Set<string>>(new Set());
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvUploading, setCSVUploading] = useState(false);
  const [csvUploadResult, setCSVUploadResult] = useState<{
    success: boolean;
    message: string;
    results?: {
      total: number;
      successful: number;
      failed: number;
      successfulChunks: string[];
      failedChunks: { chunk_id: string; error: string }[];
    };
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<chunkFormData>({
    id: '',
    content: '',
    sourceDocument: '',
    topicTags: [],
    dataTypeTags: [],
    topicTagsInput: '',
    dataTypeTagsInput: '',
  });

  // Natural sort function for chunk IDs
  const naturalSort = (a: string, b: string): number => {
    // Split the strings into parts (text and numbers)
    const regex = /(\d+)|(\D+)/g;
    const aParts = a.match(regex) || [];
    const bParts = b.match(regex) || [];
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      if (!aParts[i]) return -1;
      if (!bParts[i]) return 1;
      
      const aIsNum = /^\d+$/.test(aParts[i]);
      const bIsNum = /^\d+$/.test(bParts[i]);
      
      if (aIsNum && bIsNum) {
        // Both are numbers, compare numerically
        const diff = parseInt(aParts[i]) - parseInt(bParts[i]);
        if (diff !== 0) return diff;
      } else {
        // At least one is text, compare alphabetically
        const diff = aParts[i].localeCompare(bParts[i]);
        if (diff !== 0) return diff;
      }
    }
    
    return 0;
  };

  const fetchChunks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching source material from database...');
      const chunksRef = collection(db, 'sourceMaterial');
      
      // Try simple query first to see if there are any documents
      const simpleSnapshot = await getDocs(chunksRef);
      console.log('Total documents in sourceMaterial collection:', simpleSnapshot.docs.length);
      
      if (simpleSnapshot.empty) {
        console.log('No documents found in sourceMaterial collection');
        setMaterial([]);
        return;
      }
      
      const materialData = simpleSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`Source Material Chunks ${doc.id}:`, data);
        return {
          id: doc.id,
          ...data,
        };
      }) as SourceMaterial[];
      
      // Sort by category on the client side
      materialData.sort((a, b) => a.id.localeCompare(b.id));
      
      setMaterial(materialData);
      setError(null);
      
    } catch (error: any) {
      console.error('Error fetching source material:', error);
      setError(`Failed to fetch source material: ${error.message}`);
      setMaterial([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChunks();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const topicTags = formData.topicTagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      const dataTypeTags = formData.dataTypeTagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const chunkData = {
        id: formData.id,
        content: formData.content,
        sourceDocument: formData.sourceDocument,
        topicTags,
        dataTypeTags,
        version: editingChunk ? (editingChunk.version + 1) : 1,
        created: editingChunk ? editingChunk.created : Timestamp.now(),
        lastUpdated: Timestamp.now(),
        status: 'pending',
      };

      if (editingChunk) {
        // Update existing question
        await updateDoc(doc(db, `sourceMaterial/${formData.id}`), chunkData);
      } else {
        // Create new question
        await setDoc(doc(db, `sourceMaterial/${formData.id}`), chunkData);
      }

      // Reset form and refresh data
      setFormData({
        id: '',
        content: '',
        sourceDocument: '',
        topicTags: [],
        dataTypeTags: [],
        topicTagsInput: '',
        dataTypeTagsInput: '',
      });
      setShowForm(false);
      setEditingChunk(null);
      await fetchChunks();
    } catch (error) {
      console.error('Error saving chunk:', error);
    }
  };

  const handleUpdateIndex = async (chunkId: string) => {
    try {
      // Add chunk to updating set
      setUpdatingChunks(prev => new Set(prev).add(chunkId));
      
      // Call the cloud function
      const updateChunkIndex = httpsCallable(functions, 'updateChunkIndex');
      const result = await updateChunkIndex({ chunkId });
      
      console.log(`Index update result for ${chunkId}:`, result);
      
      // Refresh the chunks to show updated status
      await fetchChunks();
      
      // Show success message (you can also add a toast notification here)
      console.log(`Successfully updated index for chunk: ${chunkId}`);
    } catch (error: any) {
      console.error(`Error updating index for chunk ${chunkId}:`, error);
      setError(`Failed to update index for chunk ${chunkId}: ${error.message}`);
    } finally {
      // Remove chunk from updating set
      setUpdatingChunks(prev => {
        const newSet = new Set(prev);
        newSet.delete(chunkId);
        return newSet;
      });
    }
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingChunk(null);
    setFormData({
      id: '',
      content: '',
      sourceDocument: '',
      topicTags: [],
      dataTypeTags: [],
      topicTagsInput: '',
      dataTypeTagsInput: '',
    });
  };

  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setCSVUploading(true);
    setCSVUploadResult(null);
    setError(null);

    try {
      // Read the file content
      const csvContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Call the cloud function
      const uploadSourceMaterialCSV = httpsCallable(functions, 'uploadSourceMaterialCSV');
      const result = await uploadSourceMaterialCSV({
        csvContent: csvContent,
        sourceDocument: file.name
      });

      const data = result.data as any;
      setCSVUploadResult(data);

      // If successful, refresh the chunks list
      if (data.success) {
        await fetchChunks();
      }

    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      setError(`Failed to upload CSV: ${error.message}`);
      setCSVUploadResult({
        success: false,
        message: `Upload failed: ${error.message}`
      });
    } finally {
      setCSVUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Source Material Management</h1>
        <div className="flex justify-between items-center">
          <input
            type="text"
            placeholder="Search materials..."
            className="px-4 py-2 border rounded-lg w-64 !bg-base-100"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex gap-3">
            <button 
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center gap-2"
              onClick={() => setShowCSVUpload(true)}
            >
              <HiArrowUpTray className="w-5 h-5" />
              Upload CSV
            </button>
            <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              onClick={() => setShowForm(true)}
            >
              Add New Chunk
            </button>
          </div>
        </div>
      </div>

      {/* Question Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    {editingChunk ? 'Edit Chunk' : 'Add New Chunk'}
                  </h2>
                  <button
                    onClick={cancelEdit}
                    className="p-2 hover:bg-base-200 rounded-full"
                  >
                    <HiXMark className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">

                  {/* Title (Optional) */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Chunk ID</label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      className="input input-bordered w-full"
                    />
                  </div>

                  {/* Source Document */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Source Document</label>
                    <input
                      type="text"
                      value={formData.sourceDocument}
                      onChange={(e) => setFormData({ ...formData, sourceDocument: e.target.value })}
                      className="input input-bordered w-full"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Content</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="textarea textarea-bordered w-full"
                      rows={3}
                      required
                    />
                  </div>

                  {/* Topic Tags */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Topic Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.topicTagsInput}
                      onChange={(e) => setFormData({ ...formData, topicTagsInput: e.target.value })}
                      className="input input-bordered w-full"
                      placeholder="e.g. nutrition, wellness, fitness"
                    />
                  </div>

                  {/* Data Type Tags */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Data Type Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.dataTypeTagsInput}
                      onChange={(e) => setFormData({ ...formData, dataTypeTagsInput: e.target.value })}
                      className="input input-bordered w-full"
                      placeholder="e.g. research, survey, clinical"
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="btn !btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <HiCheck className="w-4 h-4" />
                      {editingChunk ? 'Update' : 'Create'} Chunk
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <HiDocumentText className="w-6 h-6" />
                    Upload CSV File
                  </h2>
                  <button
                    onClick={() => {
                      setShowCSVUpload(false);
                      setCSVUploadResult(null);
                    }}
                    className="p-2 hover:bg-base-200 rounded-full"
                  >
                    <HiXMark className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* CSV Format Instructions */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">CSV Format Requirements:</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">Your CSV file must have the following columns:</p>
                    <ul className="list-disc list-inside text-sm text-blue-700 dark:text-blue-300 space-y-1">
                      <li><code className="bg-base-100 px-1 rounded">chunk_id</code> - Unique identifier for the chunk</li>
                      <li><code className="bg-base-100 px-1 rounded">content</code> - The text content of the chunk</li>
                      <li><code className="bg-base-100 px-1 rounded">topic_tags</code> - Comma-separated topic tags</li>
                      <li><code className="bg-base-100 px-1 rounded">data_type_tags</code> - Comma-separated data type tags</li>
                    </ul>
                  </div>

                  {/* File Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Select CSV File</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      disabled={csvUploading}
                      className="block w-full text-sm text-base-content border border-base-300 rounded-lg cursor-pointer bg-base-200 focus:outline-none"
                    />
                  </div>

                  {/* Upload Progress */}
                  {csvUploading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-3 text-base-content/80">Uploading CSV...</span>
                    </div>
                  )}

                  {/* Upload Results */}
                  {csvUploadResult && (
                    <div className={`rounded-lg p-4 ${
                      csvUploadResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                    }`}>
                      <h3 className={`font-semibold mb-2 ${
                        csvUploadResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                      }`}>
                        {csvUploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                      </h3>
                      <p className={`text-sm mb-2 ${
                        csvUploadResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                      }`}>
                        {csvUploadResult.message}
                      </p>
                      
                      {csvUploadResult.results && (
                        <div className="space-y-2">
                          <div className="text-sm text-base-content/80">
                            <span className="font-medium">Total Records:</span> {csvUploadResult.results.total}
                          </div>
                          <div className="text-sm text-green-700">
                            <span className="font-medium">Successful:</span> {csvUploadResult.results.successful}
                          </div>
                          {csvUploadResult.results.failed > 0 && (
                            <>
                              <div className="text-sm text-red-700">
                                <span className="font-medium">Failed:</span> {csvUploadResult.results.failed}
                              </div>
                              {csvUploadResult.results.failedChunks.length > 0 && (
                                <div className="mt-2 max-h-32 overflow-y-auto">
                                  <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Failed Chunks:</p>
                                  {csvUploadResult.results.failedChunks.map((chunk, idx) => (
                                    <div key={idx} className="text-xs text-red-700 ml-2">
                                      • {chunk.chunk_id}: {chunk.error}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Modal Actions */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <button
                      onClick={() => {
                        setShowCSVUpload(false);
                        setCSVUploadResult(null);
                      }}
                      className="btn !btn-ghost"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Statistics */}
      {(() => {
        const filteredChunks = sourceMaterial.filter(chunk => {
          if (!searchTerm) return true;
          const searchLower = searchTerm.toLowerCase();
          return (
            chunk.id.toLowerCase().includes(searchLower) ||
            chunk.content.toLowerCase().includes(searchLower) ||
            chunk.sourceDocument?.toLowerCase().includes(searchLower) ||
            chunk.topicTags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
            chunk.dataTypeTags?.some(tag => tag.toLowerCase().includes(searchLower))
          );
        });
        
        return (
          <>
            <div className="mb-4 p-4 bg-blue-500/10 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                📊 {searchTerm 
                  ? `Showing ${filteredChunks.length} of ${sourceMaterial.length} source material chunks`
                  : `Found ${sourceMaterial.length} source material chunks total`
                }
              </p>
            </div>

            <div className="grid gap-4">
              {filteredChunks
                .sort((a, b) => naturalSort(a.id, b.id))
                .map(chunk => (
          <div key={chunk.id} className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold">{chunk.id}</h3>
                  <span className={`px-2 py-1 rounded text-sm capitalize ${
                    chunk.status === 'active' 
                      ? 'bg-green-500/15 text-green-700 dark:text-green-300' 
                      : 'bg-base-200 text-base-content'
                  }`}>
                    {chunk.status}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdateIndex(chunk.id)}
                  disabled={updatingChunks.has(chunk.id)}
                  className="btn btn-sm !bg-green-500/15 hover:!bg-green-500/25 !text-green-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Update vector index for this chunk"
                >
                  <HiArrowPath className={`w-4 h-4 ${updatingChunks.has(chunk.id) ? 'animate-spin' : ''}`} />
                  {updatingChunks.has(chunk.id) ? 'Updating...' : 'Update Index'}
                </button>
                <button
                  onClick={() => {
                    setEditingChunk(chunk);
                    setFormData({
                      id: chunk.id,
                      content: chunk.content,
                      sourceDocument: chunk.sourceDocument,
                      topicTags: chunk.topicTags || [],
                      dataTypeTags: chunk.dataTypeTags || [],
                      topicTagsInput: (chunk.topicTags || []).join(', '),
                      dataTypeTagsInput: (chunk.dataTypeTags || []).join(', '),
                    });
                    setShowForm(true);
                  }}
                  className="btn btn-sm !bg-blue-500/15 hover:!bg-blue-500/25 !text-blue-700 flex items-center gap-2"
                >
                  <HiPencilSquare className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>
            
            {/* Tags Display */}
            <div className="mb-3 space-y-2">
              {chunk.topicTags && chunk.topicTags.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-base-content/80 mr-2">Topics:</span>
                  <div className="inline-flex flex-wrap gap-1">
                    {chunk.topicTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {chunk.dataTypeTags && chunk.dataTypeTags.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-base-content/80 mr-2">Data Types:</span>
                  <div className="inline-flex flex-wrap gap-1">
                    {chunk.dataTypeTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-purple-500/15 text-purple-700 dark:text-purple-400 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <p className="text-base-content line-clamp-4">{chunk.content}</p>
            
            {/* Embedding Status */}
            <div className="mt-3 pt-3 border-t border-base-300">
              <div className="flex items-center gap-4 text-sm text-base-content/70">
                <span className="font-medium">Vector Index:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  chunk.vectorIndexed 
                    ? 'bg-green-500/15 text-green-700 dark:text-green-300' 
                    : 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400'
                }`}>
                  {chunk.vectorIndexed ? 'Indexed' : 'Not Indexed'}
                </span>
                {chunk.embeddingGeneratedAt && (
                  <span className="text-xs">
                    Last updated: {chunk.embeddingGeneratedAt.toDate ? chunk.embeddingGeneratedAt.toDate().toLocaleString() : 'N/A'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredChunks.length === 0 && (
        <div className="text-center py-8 text-base-content/60">
          {searchTerm 
            ? `No source material found matching "${searchTerm}"`
            : 'No source material found. Click "Upload CSV" or "Add New Chunk" to get started.'
          }
        </div>
      )}
    </>
  );
})()}
    </div>
  );
};

export default SourceMaterialManagement;